import React from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import {
  Button,
  MarkdownEditor,
  MarkdownPreview,
  Page,
  QueryResult,
  Toast,
  useLocalStorage,
} from "@sscs/ui";
import { EditIcon, VerticalSplitIcon, VisibilityIcon } from "@sscs/ui/icons";
import { getClassName, getQueryStatus } from "@sscs/utils";
import { AiButton } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useIsAiFeatureEnabled } from "Hooks";
import { useGetPackageQuery, useUpdatePackageMutation } from "Services";
import { apiErrorToast } from "Utils";
import { useGeneratePackageOverviewMutation } from "../../services/projects.api";
import styles from "./about.module.scss";
import EditorCard from "./EditorCard/EditorCard";
import {
  EDITOR_MODE,
  EDITOR_MODE_STORAGE_KEY,
  FIELD_LABEL,
  getDraftStorageKey,
  isEditorMode,
} from "./utils";

const About = (): React.JSX.Element => {
  const { groupId, packageId } = useParams();
  const {
    data: packageInfo,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetPackageQuery(
    packageId
      ? {
          packageId: packageId,
        }
      : skipToken,
  );
  const [updatePackage] = useUpdatePackageMutation();
  const [generateOverview] = useGeneratePackageOverviewMutation();
  const canEditPackage = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });
  const isAiEnabled = useIsAiFeatureEnabled(
    "generate_package_overview_enabled",
  );
  const [storedMode, setEditorMode] = useLocalStorage(EDITOR_MODE_STORAGE_KEY);
  const defaultMode = canEditPackage ? EDITOR_MODE.split : EDITOR_MODE.preview;
  const editorMode = isEditorMode(storedMode) ? storedMode : defaultMode;
  const [editedDescription, setEditedDescription] = useLocalStorage(
    getDraftStorageKey(packageId ?? "", "description"),
  );
  const [editedOverview, setEditedOverview] = useLocalStorage(
    getDraftStorageKey(packageId ?? "", "overview"),
  );
  const isDescriptionDirty =
    editedDescription !== null &&
    packageInfo !== undefined &&
    packageInfo.description !== editedDescription;
  const isOverviewDirty =
    editedOverview !== null &&
    packageInfo !== undefined &&
    packageInfo.overview !== editedOverview;

  const handleGenerateAbout = async (): Promise<void> => {
    if (!packageInfo) {
      return;
    }

    const progressHandle = Toast.progress({
      title: "Generating description...",
    });

    try {
      const packageOverview = await generateOverview({
        packageId: packageInfo.package_id,
      }).unwrap();

      if (!packageOverview.identified) {
        Toast.show({
          title: "Couldn't identify this software",
          description: "Write the description manually.",
          type: "warning",
        });
        return;
      }

      if (packageOverview.summary !== null) {
        setEditedDescription(packageOverview.summary);
      }
      if (packageOverview.overview !== null) {
        setEditedOverview(packageOverview.overview);
      }
      Toast.show({
        title: "Description generated",
        description: "Review it before saving.",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not generate package description");
    } finally {
      progressHandle.dismiss();
    }
  };

  const handleSave = async (
    property: keyof typeof FIELD_LABEL,
    data: string | null,
  ) => {
    if (!packageInfo || data === null) {
      return;
    }

    try {
      await updatePackage({
        packageId: packageInfo.package_id,
        body: {
          [property]: data,
        },
      }).unwrap();

      if (property === "description") setEditedDescription(null);
      else setEditedOverview(null);
      Toast.show({
        title: `${FIELD_LABEL[property]} saved`,
        type: "success",
      });
    } catch (error) {
      apiErrorToast(
        error,
        `Could not save ${FIELD_LABEL[property].toLowerCase()}`,
      );
    }
  };

  return (
    <QueryResult
      status={getQueryStatus({
        data: packageInfo,
        isFetching,
        isLoading,
        isError,
        error,
      })}
    >
      <Page.Body className={styles.body}>
        {canEditPackage && (
          <div className={styles.controls}>
            <AiButton isAiEnabled={isAiEnabled} onClick={handleGenerateAbout} />
            <div className={styles["editor-mode"]}>
              <Button
                variant="text"
                color="secondary"
                startIcon={<EditIcon />}
                aria-pressed={editorMode === EDITOR_MODE.edit}
                onClick={() => setEditorMode(EDITOR_MODE.edit)}
                className={getClassName(styles["mode-button"], {
                  [styles["selected-mode"]]: editorMode === EDITOR_MODE.edit,
                })}
              >
                Edit
              </Button>
              <Button
                variant="text"
                color="secondary"
                startIcon={<VerticalSplitIcon />}
                aria-pressed={editorMode === EDITOR_MODE.split}
                onClick={() => setEditorMode(EDITOR_MODE.split)}
                className={getClassName(styles["mode-button"], {
                  [styles["selected-mode"]]: editorMode === EDITOR_MODE.split,
                })}
              >
                Split
              </Button>
              <Button
                variant="text"
                color="secondary"
                startIcon={<VisibilityIcon />}
                aria-pressed={editorMode === EDITOR_MODE.preview}
                onClick={() => setEditorMode(EDITOR_MODE.preview)}
                className={getClassName(styles["mode-button"], {
                  [styles["selected-mode"]]: editorMode === EDITOR_MODE.preview,
                })}
              >
                Preview
              </Button>
            </div>
          </div>
        )}
        <div className={styles.editors}>
          <EditorCard
            title={FIELD_LABEL.description}
            subtitle="Plaintext of up to 500 characters displayed when browsing catalog"
            editorMode={editorMode}
            isDirty={isDescriptionDirty}
            onDiscard={() => setEditedDescription(null)}
            onSave={() => handleSave("description", editedDescription)}
            editor={
              <MarkdownEditor
                toolbar={false}
                className={styles["markdown-editor"]}
                textareaProps={{
                  "aria-label": FIELD_LABEL.description,
                  value: editedDescription ?? packageInfo?.description ?? "",
                  onChange: (event) => setEditedDescription(event.target.value),
                  placeholder: isAiEnabled
                    ? "Provide short description of up to 500 characters for this package, or use AI to generate it."
                    : "Provide short description of up to 500 characters for this package.",
                  maxLength: 500,
                }}
              />
            }
            preview={
              <div className={styles.preview}>
                {editedDescription ?? packageInfo?.description}
              </div>
            }
          />
          <EditorCard
            title={FIELD_LABEL.overview}
            subtitle="Detailed description with full markdown support"
            editorMode={editorMode}
            isDirty={isOverviewDirty}
            onDiscard={() => setEditedOverview(null)}
            onSave={() => handleSave("overview", editedOverview)}
            editor={
              <MarkdownEditor
                className={styles["markdown-editor"]}
                textareaProps={{
                  "aria-label": FIELD_LABEL.overview,
                  value: editedOverview ?? packageInfo?.overview ?? "",
                  onChange: (event) => setEditedOverview(event.target.value),
                  placeholder: isAiEnabled
                    ? "Write the full description for this package, or use AI to generate it."
                    : "Write the full description for this package.",
                }}
              />
            }
            preview={
              <MarkdownPreview
                text={editedOverview ?? packageInfo?.overview ?? ""}
                className={styles.preview}
              />
            }
          />
        </div>
      </Page.Body>
    </QueryResult>
  );
};

export default About;
